import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Divider,
  Grid,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import * as xlsx from 'xlsx';
import axios from 'axios';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  ErrorOutline as ErrorIcon,
  CheckCircle as SuccessIcon,
  ArrowForward as ArrowIcon,
  Settings as SettingsIcon,
  Build as BuildIcon,
  PlayCircleFilled as RunIcon,
  Report as ReportIcon,
  LocalShipping as ShippingIcon,
  FolderSpecial as ProjectsIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const wizardSteps = ['Select Import Module', 'Upload & Validate Data', 'Process Migration'];

const modulesConfig = {
  // Phase 1: Setup & Foundations
  'projects': {
    name: '1. Projects Master',
    phase: 'Foundations',
    columns: [
      'Project Key (Required)', 'Project Name (Required)', 'Project Type (Required)', 'Status (Required)',
      'Start Date (YYYY-MM-DD) (Required)', 'End Date (YYYY-MM-DD) (Required)', 'Budget (INR) (Required)',
      'Management Template (Required)', 'Project Manager Employee ID (Required)', 'Tech Lead Employee ID',
      'Product Manager Employee ID', 'Product Owner Employee ID', 'Cost Center Code', 'Description', 'Notes', 'Completion % (0-100)'
    ],
    sampleData: [{
      'Project Key (Required)': 'PRJ-01',
      'Project Name (Required)': 'PulseHR V2 Upgrade',
      'Project Type (Required)': 'Software',
      'Status (Required)': 'In Progress',
      'Start Date (YYYY-MM-DD) (Required)': '2026-01-01',
      'End Date (YYYY-MM-DD) (Required)': '2026-12-31',
      'Budget (INR) (Required)': 500000,
      'Management Template (Required)': 'Scrum',
      'Project Manager Employee ID (Required)': 'EMP1001',
      'Tech Lead Employee ID': 'EMP1002',
      'Product Manager Employee ID': 'EMP1003',
      'Product Owner Employee ID': 'EMP1004',
      'Cost Center Code': 'CC-ENG',
      'Description': 'Migration and enhancement of core enterprise HR & Project modules.',
      'Notes': 'Priority migration project',
      'Completion % (0-100)': 35
    }]
  },

  // Phase 2: Teams & Allocation
  'teams': {
    name: '2. Project Teams & Allocations',
    phase: 'Resource Teams',
    columns: [
      'Project Key (Required)', 'Team Name (Required)', 'Team Type (Required)', 'Employee or Contractor ID (Required)',
      'Member Type (Employee/Contractor) (Required)', 'Role (Required)', 'Allocation % (1-100) (Required)',
      'Is Active (TRUE/FALSE)', 'Start Date (YYYY-MM-DD) (Required)', 'End Date (YYYY-MM-DD)'
    ],
    sampleData: [{
      'Project Key (Required)': 'PRJ-01',
      'Team Name (Required)': 'Core Development Team',
      'Team Type (Required)': 'Development',
      'Employee or Contractor ID (Required)': 'EMP1002',
      'Member Type (Employee/Contractor) (Required)': 'Employee',
      'Role (Required)': 'Tech Lead',
      'Allocation % (1-100) (Required)': 100,
      'Is Active (TRUE/FALSE)': 'TRUE',
      'Start Date (YYYY-MM-DD) (Required)': '2026-01-01',
      'End Date (YYYY-MM-DD)': '2026-12-31'
    }]
  },

  // Phase 3: Requirements & Task Execution
  'issues': {
    name: '3. Requirements & Tasks',
    phase: 'Execution',
    sheetsInfo: [
      { name: '1_Epics', columns: ['Epic Key (Required)', 'Project Key (Required)', 'Summary (Required)', 'Description', 'Priority', 'Severity', 'Status', 'Assignee Employee ID', 'Reporter Employee ID', 'Reviewer Employee ID', 'Watcher Employee ID', 'Labels', 'Start Date', 'End Date'] },
      { name: '2_Stories', columns: ['Story Key (Required)', 'Project Key (Required)', 'Epic Key (Required)', 'Summary (Required)', 'Description', 'Story Points', 'Priority', 'Severity', 'Status', 'Assignee Employee ID', 'Reporter Employee ID', 'Reviewer Employee ID', 'Watcher Employee ID', 'Labels', 'Start Date', 'End Date'] },
      { name: '3_Tasks', columns: ['Task Key (Required)', 'Project Key (Required)', 'Story Key (Required)', 'Summary (Required)', 'Description', 'Priority', 'Severity', 'Status', 'Assignee Employee ID', 'Reporter Employee ID', 'Reviewer Employee ID', 'Watcher Employee ID', 'Labels', 'Time Estimate', 'Time Spent', 'Start Date', 'End Date'] },
      { name: '4_Bugs', columns: ['Bug Key (Required)', 'Project Key (Required)', 'Task Key (Required)', 'Summary (Required)', 'Description', 'Priority', 'Severity', 'Status', 'Bug Status', 'Assignee Employee ID', 'Reporter Employee ID', 'Reviewer Employee ID', 'Watcher Employee ID', 'Labels', 'Found In Build', 'Fixed In Build', 'Start Date', 'End Date'] }
    ],
    columns: [
      'Multi-Sheet Excel Template (1_Epics, 2_Stories, 3_Tasks, 4_Bugs)',
      'Product Owner & Lead roles are automatically inherited from Project Manager and Project table properties.'
    ],
    sampleData: []
  },

  // Phase 4: Dependencies
  'dependencies': {
    name: '4. Issue Link Dependencies',
    phase: 'Execution',
    columns: ['Source Issue Key (Required)', 'Target Issue Key (Required)', 'Link Type (blocks/is blocked by/relates to/duplicates)'],
    sampleData: [{
      'Source Issue Key (Required)': 'PRJ-102',
      'Target Issue Key (Required)': 'PRJ-101',
      'Link Type (blocks/is blocked by/relates to/duplicates) (Required)': 'blocks'
    }]
  },

  // Phase 5: Sprints & Iterations
  'sprints': {
    name: '5. Sprints & Iterations',
    phase: 'Planning',
    sheetsInfo: [
      { name: 'Sprints', columns: ['Sprint Name (Required)', 'Project Key (Required)', 'Sprint Goal', 'Start Date (YYYY-MM-DD)', 'End Date (YYYY-MM-DD)', 'Duration (Weeks)', 'Status (FUTURE/ACTIVE/COMPLETED) (Required)'] },
      { name: 'Sprint_Issues', columns: ['Sprint Name (Required)', 'Issue Key (Required)', 'Status (To Do/In Progress/Done)', 'Assignee Employee ID', 'Story Points', 'Start Date (YYYY-MM-DD)', 'End Date (YYYY-MM-DD)', 'Actual Start Date (YYYY-MM-DD)', 'Completed Date (YYYY-MM-DD)'] }
    ],
    columns: [
      'Multi-Sheet Excel Template (Sprints, Sprint_Issues)',
      'Define sprints and map issues (Epics, Stories, Tasks, Bugs) directly to sprints.'
    ],
    sampleData: []
  }
};

const ProjectMigrationPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedModule, setSelectedModule] = useState('projects');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [uploadStatus, setUploadStatus] = useState({ loading: false, success: null, error: null, errorsList: [], results: null });

  const token = localStorage.getItem('token');
  const authHeaders = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const handleDownloadTemplate = async () => {
    if (!selectedModule) return;
    try {
      const response = await axios.get(`${API_URL}/api/project-migration/template?feature=${selectedModule}`, {
        ...authHeaders,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedModule}_migration_template_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      //console.error('Error downloading template:', err);
      // Fallback client-side template download
      const config = modulesConfig[selectedModule];
      if (config && config.sampleData.length > 0) {
        const ws = xlsx.utils.json_to_sheet(config.sampleData);
        const wb = xlsx.utils.book_new();
        xlsx.utils.book_append_sheet(wb, ws, 'Template');
        xlsx.writeFile(wb, `${selectedModule}_import_template.xlsx`);
      }
    }
  };

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    setUploadedFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = xlsx.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet);
        setPreviewData(json.slice(0, 5));
      } catch (err) {
        //console.error('Failed to parse file preview:', err);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  const handleNext = () => {
    if (activeStep === 0 && !selectedModule) return;
    if (activeStep === 1 && !uploadedFile) return;

    if (activeStep === 1) {
      handleUpload();
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedModule('projects');
    setUploadedFile(null);
    setPreviewData([]);
    setUploadStatus({ loading: false, success: null, error: null, errorsList: [], results: null });
  };

  const handleUpload = async () => {
    setUploadStatus({ loading: true, success: null, error: null, errorsList: [], results: null });
    const formData = new FormData();
    formData.append('file', uploadedFile);

    try {
      const response = await axios.post(`${API_URL}/api/project-migration/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setUploadStatus({
        loading: false,
        success: response.data.message || 'Project data successfully imported!',
        error: null,
        errorsList: [],
        results: response.data.results
      });
    } catch (err) {
      //console.error(err);
      const errorResponse = err.response?.data;
      setUploadStatus({
        loading: false,
        success: null,
        error: errorResponse?.message || 'Error occurred during migration committal.',
        errorsList: errorResponse?.errors || [],
        results: null
      });
    }
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'Foundations': return <SettingsIcon color="primary" />;
      case 'Planning': return <BuildIcon color="secondary" />;
      case 'Resource Teams': return <ProjectsIcon color="action" />;
      case 'Execution': return <RunIcon color="success" />;
      case 'Full Master': return <ShippingIcon color="info" />;
      default: return <ReportIcon color="warning" />;
    }
  };

  return (
    <Box sx={{ maxWidth: '1400px', margin: '0 auto', padding: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1,  }}>
        Data Migration
      </Typography>
      <Typography variant="body2" sx={{ color: '#475569', mb: 4 }}>
        Phase-based enterprise data pipeline. Download standard templates, verify pre-fetched records, and push safely into database transactions.
      </Typography>

      <Grid container spacing={4}>
        {/* Left column: Visual Flowchart list */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1E293B' }}>
              Migration Flow Map
            </Typography>
            <Typography variant="caption" display="block" sx={{ color: '#64748B', mb: 3 }}>
              Please upload spreadsheets in this exact topological order to satisfy foreign key relationships.
            </Typography>

            <List dense disablePadding>
              {Object.entries(modulesConfig).map(([key, config]) => {
                const isSelected = selectedModule === key;
                return (
                  <ListItem
                    key={key}
                    onClick={() => setSelectedModule(key)}
                    sx={{
                      mb: 1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      border: isSelected ? '2px solid #6366F1' : '1px solid #E2E8F0',
                      bgcolor: isSelected ? '#EEF2F6' : 'transparent',
                      p: 1.5,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {getPhaseIcon(config.phase)}
                    </ListItemIcon>
                    <ListItemText
                      primary={config.name}
                      secondary={
                        <Chip
                          label={config.phase}
                          size="small"
                          sx={{
                            mt: 0.5,
                            fontSize: '0.65rem',
                            bgcolor:
                              config.phase === 'Foundations' ? '#EFF6FF' :
                                config.phase === 'Planning' ? '#FEF3C7' :
                                  config.phase === 'Resource Teams' ? '#F5F3FF' :
                                    config.phase === 'Execution' ? '#ECFDF5' : '#F0FDFA',
                            color:
                              config.phase === 'Foundations' ? '#1D4ED8' :
                                config.phase === 'Planning' ? '#B45309' :
                                  config.phase === 'Resource Teams' ? '#6D28D9' :
                                    config.phase === 'Execution' ? '#047857' : '#0F766E'
                          }}
                        />
                      }
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: isSelected ? 700 : 500,
                        color: isSelected ? '#6366F1' : '#334155'
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Grid>

        {/* Right column: Import Wizard Action interface */}
        <Grid item xs={12} md={8}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {wizardSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Card sx={{ border: '1px solid #E5E7EB', boxShadow: 'none', borderRadius: 3 }}>
            <CardContent sx={{ p: 4 }}>
              {activeStep === 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#1E293B' }}>
                    1. Select Target Project Management Step
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Step / Project Table Module</InputLabel>
                    <Select
                      value={selectedModule}
                      onChange={(e) => setSelectedModule(e.target.value)}
                      label="Step / Project Table Module"
                    >
                      {Object.entries(modulesConfig).map(([key, config]) => (
                        <MenuItem key={key} value={key}>
                          {config.name} ({config.phase})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedModule && (
                    <Box sx={{ p: 3, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px dashed #CBD5E1' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#334155' }}>
                        Required & Enriched Excel Columns:
                      </Typography>
                      {modulesConfig[selectedModule].sheetsInfo ? (
                        modulesConfig[selectedModule].sheetsInfo.map((sh, sIdx) => (
                          <Box key={sIdx} sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: '#6366F1' }}>
                              Sheet: {sh.name}
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', color: '#475569', mb: 1, wordBreak: 'break-all' }}>
                              {sh.columns.join(', ')}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mb: 3, color: '#475569', wordBreak: 'break-all' }}>
                          {modulesConfig[selectedModule].columns.join(', ')}
                        </Typography>
                      )}
                      <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadTemplate}
                        size="medium"
                        sx={{ bgcolor: '#0F172A', '&:hover': { bgcolor: '#1E293B' }, mt: 1 }}
                      >
                        Download Step Template (.xlsx)
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {activeStep === 1 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#1E293B' }}>
                    2. Upload Filled Template File
                  </Typography>

                  <div
                    {...getRootProps()}
                    style={{
                      border: '2px dashed #6366F1',
                      borderRadius: '12px',
                      padding: '50px 30px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: isDragActive ? '#EEF2F6' : '#F8FAFC',
                      transition: 'background-color 0.2s ease',
                      marginBottom: '24px'
                    }}
                  >
                    <input {...getInputProps()} />
                    <UploadIcon sx={{ fontSize: 56, color: '#6366F1', mb: 1.5 }} />
                    {uploadedFile ? (
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#10b981' }}>
                        File Selected: {uploadedFile.name} ({Math.round(uploadedFile.size / 1024)} KB)
                      </Typography>
                    ) : (
                      <Typography variant="body1" sx={{ color: '#475569' }}>
                        Drag & Drop or Click to Upload Step Sheet
                      </Typography>
                    )}
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                      Accepted formats: CSV, Excel (.xlsx, .xls). Capacity: 500+ records.
                    </Typography>
                  </div>

                  {previewData.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#334155' }}>
                        Upload Preview (First 5 Rows):
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 250 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              {Object.keys(previewData[0]).map((key) => (
                                <TableCell key={key} sx={{ fontWeight: 700, bgcolor: '#F8FAFC', color: '#475569' }}>
                                  {key}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {previewData.map((row, idx) => (
                              <TableRow key={idx}>
                                {Object.values(row).map((val, cellIdx) => (
                                  <TableCell key={cellIdx}>{String(val)}</TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Box>
              )}

              {activeStep === 2 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#1E293B' }}>
                    3. Migration Committal Status
                  </Typography>

                  {uploadStatus.loading && (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <CircularProgress size={44} sx={{ mb: 2, color: '#6366F1' }} />
                      <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
                        Executing safe database insertion and applying AES-256 transparent column encryption...
                      </Typography>
                      <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />
                    </Box>
                  )}

                  {uploadStatus.success && (
                    <Box>
                      <Alert severity="success" icon={<SuccessIcon fontSize="inherit" />} sx={{ mb: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Migration Successful!
                        </Typography>
                        {uploadStatus.success}
                      </Alert>

                      {uploadStatus.results && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={6} sm={4}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 2, textAlign: 'center' }}>
                              <Typography variant="h5" fontWeight="bold" color="success.main">{uploadStatus.results.projectsCreated || 0}</Typography>
                              <Typography variant="caption" fontWeight="bold" color="text.secondary">Projects Created</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 2, textAlign: 'center' }}>
                              <Typography variant="h5" fontWeight="bold" color="success.main">{uploadStatus.results.sprintsCreated || 0}</Typography>
                              <Typography variant="caption" fontWeight="bold" color="text.secondary">Sprints Created</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 2, textAlign: 'center' }}>
                              <Typography variant="h5" fontWeight="bold" color="success.main">{uploadStatus.results.teamMembersAssigned || 0}</Typography>
                              <Typography variant="caption" fontWeight="bold" color="text.secondary">Team Members</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 2, textAlign: 'center' }}>
                              <Typography variant="h5" fontWeight="bold" color="success.main">{uploadStatus.results.issuesCreated || 0}</Typography>
                              <Typography variant="caption" fontWeight="bold" color="text.secondary">Issues & Tasks</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 2, textAlign: 'center' }}>
                              <Typography variant="h5" fontWeight="bold" color="success.main">{uploadStatus.results.dependenciesCreated || 0}</Typography>
                              <Typography variant="caption" fontWeight="bold" color="text.secondary">Dependencies</Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 2, textAlign: 'center' }}>
                              <Typography variant="h5" fontWeight="bold" color="success.main">{uploadStatus.results.roadmapItemsCreated || 0}</Typography>
                              <Typography variant="caption" fontWeight="bold" color="text.secondary">Roadmap Items</Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  )}

                  {uploadStatus.error && (
                    <Box>
                      <Alert severity="error" icon={<ErrorIcon fontSize="inherit" />} sx={{ mb: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Migration Refused / Database Integrity Rule Broken
                        </Typography>
                        {uploadStatus.error}
                      </Alert>

                      {uploadStatus.errorsList.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" color="error" sx={{ mb: 1.5, fontWeight: 700 }}>
                            Validation Corrections Checklist:
                          </Typography>
                          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 250 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 700, width: 80 }}>Row #</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Error Details</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {uploadStatus.errorsList.map((err, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell sx={{ color: '#EF4444', fontWeight: 700 }}>{err.row}</TableCell>
                                    <TableCell>{err.error}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Grid container justifyContent="space-between">
                <Grid item>
                  {activeStep > 0 && activeStep < 2 && (
                    <Button onClick={handleBack} variant="outlined">
                      Back
                    </Button>
                  )}
                </Grid>
                <Grid item>
                  {activeStep < 2 ? (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={
                        (activeStep === 0 && !selectedModule) ||
                        (activeStep === 1 && !uploadedFile)
                      }
                      sx={{ bgcolor: '#6366F1', '&:hover': { bgcolor: '#4F46E5' } }}
                    >
                      {activeStep === 1 ? 'Start Migration' : 'Next'}
                    </Button>
                  ) : (
                    !uploadStatus.loading && (
                      <Button onClick={handleReset} variant="contained" sx={{ bgcolor: '#6366F1' }}>
                        Select Next Flow Step
                      </Button>
                    )
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectMigrationPage;
